import { BaseListViewStore, BooleanToggleCellData, checkPermissions, CloseOutsCellData } from 'core/stores';
import { action, runInAction } from 'mobx';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { observable, makeObservable, reaction } from 'mobx';
import { notificationError, TableFilterUtility, isSuperAdminOrganization } from 'core/utils';
import { WarehouseOrganizationInfoStore } from './';
import { sourceOfChange } from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { LookupStore } from 'modules/lookup/stores';
import _ from 'lodash';

class WarehouseListViewStore extends BaseListViewStore {
	@observable applicationSort = [{ field: 'applicationTitle', dir: 'asc' }];
	application;

	activationConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.changeOrganizationActive(this.application, !this.application.isActive);
		},
	});

	showActions = true;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ApplicationStore(rootStore),
			queryParams: {
				embed: ['warehouseOrganizationInfo', 'closeOuts'],
			},
			findFunction: 'findWarehouse',
			tableData: {
				columns: [
					{
						key: 'applicationTitle',
						title: 'WAREHOUSELISTVIEW.TITLE',
						sortable: true,
						onClick: warehouse => this.routes.edit(warehouse.id),
						selected: true,
					},
					{
						key: 'identifier',
						title: 'WAREHOUSELISTVIEW.IDENTIFIER',
						sortable: true,
					},
					{
						key: 'warehouseOrganizationInfo.isAvailable',
						title: 'WAREHOUSELISTVIEW.AVAILABLE',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.isAvailableToggle(warehouse, field),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.useAutoExitScan',
						title: 'WAREHOUSELISTVIEW.AUTOEXITSCAN',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.useAutoExitScanToggle(warehouse, field),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.acceptingReturns',
						title: 'WAREHOUSELISTVIEW.ACCEPTINGRETURNS',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.acceptingReturnsToggle(warehouse, field),
						isClickable: true,
					},
					{
						key: 'closeOuts[0].palletizingCloseOut',
						title: 'CLOSEOUTPALLETIZING',
						selected: true,
						dataCell: CloseOutsCellData,
						isClickable: false,
						nullAsFalse: true,
					},
					{
						key: 'closeOuts[0].exitScanCloseOut',
						title: 'CLOSEOUTEXITSCAN',
						selected: true,
						dataCell: CloseOutsCellData,
						isClickable: false,
						nullAsFalse: true,
					},
				],
				sortObject: [
					{
						field: 'applicationTitle',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: application => this.routes.edit(application.id),
					onChangeOrganizationActive: application => this.openConfirmModal(application, !application.isActive),
					onDelete: application => this.openDeleteConfirmModal(application),
				},
			},
			routes: {
				create: () => this.rootStore.routerStore.goTo('master.app.main.warehouse.create'),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.warehouse.edit', {
						id,
					}),
			},
			permissions: {
				update: () => this.authStore.hasPermission(),
				create: () => this.authStore.hasPermission(),
				remove: () => this.authStore.hasPermission(),
				read: () => this.authStore.hasPermission(),
				full: () => this.authStore.hasPermission(),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(this.rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findWarehouse);
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.warehouse.list') {
					return;
				}

				const { editId, create } = state.queryParams;

				this.editModal.setOpen(editId !== undefined || create !== undefined);
			}
		);
		this.warehouseOrganizationInfoStore = new WarehouseOrganizationInfoStore(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			service: this.listStore.service,
		});
		this.showActions = checkPermissions(this).actionsAllowed;
		this.setButtons();
		this.setSearchVisible();
	}

	@action.bound
	async isAvailableToggle(warehouse, field) {
		this.suspend();
		try {
			const warehouseOrganizationInfo = _.get(warehouse, field.split('.')[0]);
			warehouseOrganizationInfo.isAvailable = !warehouseOrganizationInfo.isAvailable;
			await this.warehouseOrganizationInfoStore.updateWarehouseOrganizationInfo(
				warehouseOrganizationInfo,
				sourceOfChange.isAvailableToggleWarehouseList
			);
			this.rootStore.notificationStore.success([
				'LOOKUPUPDATE.WAREHOUSEORGANIZATIONINFO',
				{ name: warehouse.applicationTitle },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound
	async useAutoExitScanToggle(warehouse, field) {
		this.suspend();
		try {
			const warehouseOrganizationInfo = _.get(warehouse, field.split('.')[0]);
			warehouseOrganizationInfo.useAutoExitScan = !warehouseOrganizationInfo.useAutoExitScan;
			await this.warehouseOrganizationInfoStore.updateWarehouseOrganizationInfo(
				warehouseOrganizationInfo,
				sourceOfChange.useAutoscanWarehouseList
			);
			this.rootStore.notificationStore.success([
				'LOOKUPUPDATE.WAREHOUSEORGANIZATIONINFO',
				{ name: warehouse.applicationTitle },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound
	async acceptingReturnsToggle(warehouse, field) {
		this.suspend();
		try {
			const warehouseOrganizationInfo = _.get(warehouse, field.split('.')[0]);
			warehouseOrganizationInfo.acceptingReturns = !warehouseOrganizationInfo.acceptingReturns;
			await this.warehouseOrganizationInfoStore.updateWarehouseOrganizationInfo(
				warehouseOrganizationInfo,
				sourceOfChange.acceptingReturns
			);
			this.rootStore.notificationStore.success([
				'LOOKUPUPDATE.WAREHOUSEORGANIZATIONINFO',
				{ name: warehouse.applicationTitle },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound async findWarehouse() {
		this.suspend();
		runInAction(() => {
			//Default sorting
			this.queryUtility.filter.warehouseOnly = true;
			this.queryUtility.filter.getOnlyTodayCloseOut = true;
		});
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					text: 'WAREHOUSEVIEW.NEW',
				},
			],
		});
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	editModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.deleteOrganization(this.selectedItem);
		},
	});

	@action.bound
	openDeleteConfirmModal = item => {
		this.selectedItem = item;
		this.deleteConfirmModal.open({
			message: ['LOOKUPDELETECONFIRM.ORGANIZATION', { name: item.applicationTitle }],
		});
	};

	@action.bound openConfirmModal(application, isReactivate) {
		this.activationConfirmModal.open({
			message: [
				isReactivate ? 'ORGANIZATION.LIST.REACTIVATECONFIRM' : 'ORGANIZATION.LIST.DEACTIVATECONFIRM',
				{ name: application.identifier },
			],
		});
		this.application = application;
	}
	@action.bound async changeOrganizationActive(application, isReactivate) {
		this.suspend();
		try {
			await this.listStore.changeStatus(application.id, isReactivate);
			this.rootStore.notificationStore.success([
				isReactivate ? 'ORGANIZATION.LIST.REACTIVATESUCCESS' : 'ORGANIZATION.LIST.DEACTIVATESUCCESS',
				{ name: application.identifier },
			]);
			this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound
	deleteOrganization = async organization => {
		this.suspend();
		try {
			await this.listStore.deleteApplication(organization);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.ORGANIZATION', { name: organization.applicationTitle }]);
			this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};
}

export default WarehouseListViewStore;



// WEBPACK FOOTER //
// ./src/modules/warehouse/stores/WarehouseListViewStore.js